import React, { useState, useEffect } from 'react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { teachersApi, timetableApi } from '../services/api';
import type { TimetableSlot, Teacher } from '../types';
import { toast } from 'react-hot-toast';

interface TimetableSlotEditModalProps {
  slot: TimetableSlot;
  onClose: () => void;
  onUpdate: () => void;
}

const TimetableSlotEditModal: React.FC<TimetableSlotEditModalProps> = ({ slot, onClose, onUpdate }) => {
  const [teachers, setTeachers] = useState<Teacher[]>([]);
  const [selectedTeacher, setSelectedTeacher] = useState<string>(slot.teacher1_code || '');
  const [selectedRoom, setSelectedRoom] = useState<string>(slot.room || '');
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [partnerClass, setPartnerClass] = useState<string | null>(null);
  const [isSwapping, setIsSwapping] = useState(false);

  // Get unique rooms from teachers
  const uniqueRooms = Array.from(new Set(teachers.map(t => t.room).filter(r => r && r.trim() !== '')));

  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      try {
        const [teachersResponse, partnersResponse] = await Promise.all([
          teachersApi.getAll(),
          timetableApi.getPartnerClasses()
        ]);
        
        setTeachers(teachersResponse.data);
        
        // Check if this class has a partner
        const partners = partnersResponse.data.partners;
        if (slot.code in partners) {
          setPartnerClass(partners[slot.code]);
        }
      } catch (error) {
        console.error('Error loading data:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [slot.code]);

  const handleSave = async () => {
    setIsSaving(true);
    try {
      await timetableApi.updateSlot(
        slot.id,
        selectedTeacher !== slot.teacher1_code ? selectedTeacher : undefined,
        selectedRoom !== slot.room ? selectedRoom : undefined
      );
      toast.success('Slot updated successfully');
      onUpdate();
      onClose();
    } catch (error) {
      console.error('Error updating slot:', error);
      toast.error('Failed to update slot. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleSwapPartner = async () => {
    if (!partnerClass) return;
    
    if (!window.confirm(`Are you sure you want to swap ${slot.code} with ${partnerClass}? This will replace the entire class slot.`)) {
      return;
    }
    
    setIsSwapping(true);
    try {
      const response = await timetableApi.swapPartnerClass(slot.id);
      toast.success(response.data.message);
      onUpdate();
      onClose();
    } catch (error: any) {
      console.error('Error swapping partner class:', error);
      const errorMessage = error.response?.data?.detail || 'Failed to swap partner class';
      toast.error(errorMessage);
    } finally {
      setIsSwapping(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm(`Are you sure you want to delete this timeslot?\n\nClass: ${slot.code}\nDay ${slot.day}, Period ${slot.period}\n\nThis action cannot be undone.`)) {
      return;
    }
    
    setIsSaving(true);
    try {
      await timetableApi.deleteSlot(slot.id);
      toast.success('Timeslot deleted successfully');
      onUpdate();
      onClose();
    } catch (error: any) {
      console.error('Error deleting slot:', error);
      const errorMessage = error.response?.data?.detail || 'Failed to delete timeslot';
      toast.error(errorMessage);
    } finally {
      setIsSaving(false);
    }
  };

  const hasChanges = selectedTeacher !== slot.teacher1_code || selectedRoom !== slot.room;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-neutral-light-gray">
          <div>
            <h2 className="text-h3 font-montserrat text-northwood-navy">Edit Timetable Slot</h2>
            <p className="text-sm text-neutral-medium-gray mt-1">
              {slot.code} - Day {slot.day}, Period {slot.period}
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-neutral-medium-gray hover:text-northwood-navy transition-colors"
          >
            <XMarkIcon className="w-6 h-6" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Current Info */}
          <div className="bg-neutral-light-gray rounded-lg p-4">
            <h3 className="font-montserrat font-semibold text-sm text-neutral-dark-gray mb-3">
              Current Assignment
            </h3>
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <span className="text-neutral-medium-gray">Class:</span>
                <span className="ml-2 font-medium text-northwood-navy">{slot.code}</span>
              </div>
              <div>
                <span className="text-neutral-medium-gray">Type:</span>
                <span className="ml-2 font-medium text-northwood-navy">{slot.type}</span>
              </div>
              <div>
                <span className="text-neutral-medium-gray">Subject:</span>
                <span className="ml-2 font-medium text-northwood-navy">{slot.subject_code || '-'}</span>
              </div>
              <div>
                <span className="text-neutral-medium-gray">Class Code:</span>
                <span className="ml-2 font-medium text-northwood-navy">{slot.class_code || '-'}</span>
              </div>
            </div>
          </div>

          {/* Teacher Selection */}
          <div>
            <label className="block text-sm font-montserrat font-medium text-neutral-dark-gray mb-2">
              Teacher <span className="text-red-500">*</span>
            </label>
            <select
              value={selectedTeacher}
              onChange={(e) => setSelectedTeacher(e.target.value)}
              disabled={isLoading || isSaving}
              className="w-full px-4 py-2 border border-northwood-silver rounded-lg focus:outline-none focus:ring-2 focus:ring-accent-teal disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <option value="">Select a teacher...</option>
              {teachers.map((teacher) => (
                <option key={teacher.code} value={teacher.code}>
                  {teacher.code} - {teacher.title} {teacher.name} {teacher.surname} 
                  {teacher.room && ` (Room: ${teacher.room})`}
                </option>
              ))}
            </select>
            {selectedTeacher !== slot.teacher1_code && (
              <p className="text-xs text-amber-600 mt-1">
                ⚠️ Changing teacher - ensure no scheduling conflicts
              </p>
            )}
          </div>

          {/* Room Selection */}
          <div>
            <label className="block text-sm font-montserrat font-medium text-neutral-dark-gray mb-2">
              Room <span className="text-red-500">*</span>
            </label>
            <select
              value={selectedRoom}
              onChange={(e) => setSelectedRoom(e.target.value)}
              disabled={isLoading || isSaving}
              className="w-full px-4 py-2 border border-northwood-silver rounded-lg focus:outline-none focus:ring-2 focus:ring-accent-teal disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <option value="">Select a room...</option>
              <option value="Mismatch">Mismatch (No assigned room)</option>
              {uniqueRooms.sort().map((room) => (
                <option key={room} value={room}>
                  {room}
                </option>
              ))}
            </select>
            {selectedRoom !== slot.room && (
              <p className="text-xs text-amber-600 mt-1">
                ⚠️ Changing room - ensure no room conflicts
              </p>
            )}
          </div>

          {/* Partner Class Swap Section */}
          {partnerClass && (
            <div className="border-t border-neutral-light-gray pt-6">
              <h3 className="font-montserrat font-semibold text-sm text-neutral-dark-gray mb-3">
                Swap with Partner Class
              </h3>
              <div className="bg-blue-50 border border-blue-300 rounded-lg p-4">
                <p className="text-sm text-blue-900 mb-3">
                  This class has a partner class: <strong>{partnerClass}</strong>
                </p>
                <p className="text-xs text-blue-700 mb-3">
                  Swapping will completely replace <strong>{slot.code}</strong> with <strong>{partnerClass}</strong> in this timeslot.
                  The system will validate that no clashes occur.
                </p>
                <button
                  onClick={handleSwapPartner}
                  disabled={isSwapping || isSaving}
                  className="w-full px-4 py-2 bg-accent-teal text-white rounded-lg hover:bg-opacity-90 transition-all disabled:opacity-50 disabled:cursor-not-allowed font-medium"
                >
                  {isSwapping ? 'Swapping...' : `Swap to ${partnerClass}`}
                </button>
              </div>
            </div>
          )}

          {/* Warning Notice */}
          {hasChanges && (
            <div className="bg-amber-50 border border-amber-300 rounded-lg p-4">
              <p className="text-sm text-amber-800">
                <strong>Note:</strong> This will only update this specific timetable slot.
              </p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between p-6 border-t border-neutral-light-gray">
          <button
            onClick={handleDelete}
            disabled={isSaving || isSwapping}
            className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed font-medium"
          >
            Delete Slot
          </button>
          <div className="flex items-center space-x-3">
            <button
              onClick={onClose}
              disabled={isSaving}
              className="px-4 py-2 text-neutral-medium-gray hover:text-neutral-dark-gray transition-colors disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={!hasChanges || !selectedTeacher || !selectedRoom || isSaving}
              className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSaving ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TimetableSlotEditModal;
